import { create } from 'zustand'
import { authAPI } from '../services/api'

export interface AdminUser {
  id: string
  username: string
  email: string
  roles: ('ADMIN' | 'PLAYER' | 'CREATOR')[]
  displayName?: string
  avatarUrl?: string
}

interface AuthState {
  isAuthenticated: boolean
  user: AdminUser | null
  token: string | null
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  setUser: (user: AdminUser) => void
  checkAuth: () => Promise<boolean>
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  user: null,
  token: null,
  
  login: async (username: string, password: string) => {
    try {
      const response = await authAPI.login({ username, password })
      
      if (!response?.data) {
        throw new Error('No data received from login response')
      }

      const { user, access_token } = response.data
      
      const userData = {
        id: user.id.toString(),
        username: user.username,
        email: user.email,
        roles: user.roles || ['ADMIN'], // Default to ADMIN role for admin portal
        displayName: user.displayName,
        avatarUrl: user.avatarUrl
      }
      
      set({
        isAuthenticated: true,
        user: userData,
        token: access_token
      })
      
      // Save to localStorage
      localStorage.setItem('admin-auth', JSON.stringify({
        isAuthenticated: true,
        user: userData,
        token: access_token
      }))
      
      return true
    } catch (error) {
      console.error('Login error:', error)
      return false
    }
  },
  
  logout: () => {
    set({
      isAuthenticated: false,
      user: null,
      token: null,
    })
    localStorage.removeItem('admin-auth')
    // Redirect to login page
    window.location.href = '/login'
  },
  
  setUser: (user: AdminUser) => {
    set({ user })
  },

  checkAuth: async () => {
    try {
      const storedAuth = localStorage.getItem('admin-auth')
      if (!storedAuth) return false
      
      const authData = JSON.parse(storedAuth)
      if (!authData.token) return false
      
      // Verify token with backend
      await authAPI.verify()
      
      set({
        isAuthenticated: true,
        user: authData.user,
        token: authData.token,
      })
      
      return true
    } catch (error) {
      console.error('Auth verification failed:', error)
      // Clear invalid auth data
      localStorage.removeItem('admin-auth')
      set({
        isAuthenticated: false,
        user: null,
        token: null,
      })
      return false
    }
  },
}))

// Initialize auth state from localStorage on app start
const initializeAuth = async () => {
  const store = useAuthStore.getState()
  await store.checkAuth()
}

// Call initialization
if (typeof window !== 'undefined') {
  initializeAuth()
}
